import React, { useEffect, useState } from 'react';
import { IonIcon } from '@ionic/react';
import { IonicInput } from 'ionic-ui';
import {
  walletOutline,
  downloadOutline,
  copyOutline,
  chatbubbleEllipsesOutline,
  documentTextOutline,
  lockClosedOutline,
  receiptOutline,
  sendOutline,
} from 'ionicons/icons';
import { formatPrice } from '../../../../utils/formatPrice';
import {
  WalletCustomerCardData,
  WalletDashboardStatus,
} from '../../../../types/wallet/Walletkyc';
import { useClipboard } from '../../../../hooks/useClipboard';
import { Link } from 'react-router-dom';
import { useDialog } from '../../../../context/Dialog';
import WalletKycDialog from './dialogs/walletKycDialogs/WalletKycDialog';
import WalletIndividualKycDialog from './dialogs/walletIndividualDialogs/WalletIndividualKycDialog';
import SendMoneyDialog from './dialogs/walletOpsDialogs/SendMoneyDialog';
import WalletTransactionsDialog from './dialogs/walletOpsDialogs/WalletTransactionsDialog';

type Props = WalletDashboardStatus | null;

const Wallet: React.FC<Props> = (props) => {
  const { openDialog } = useDialog();
  const relatedCustomers = props?.related_customers || [];
  const [activeCustomerId, setActiveCustomerId] = useState<number>(
    props?.customer_id || 0
  );
  const [activeWallet, setActiveWallet] = useState<number>(0);
  const selectedCustomer =
    relatedCustomers.find(
      (customer) => customer.customer_id === activeCustomerId
    ) ||
    relatedCustomers[0] ||
    null;
  const wallets = selectedCustomer?.wallets || [];
  const selectedWallet = wallets[activeWallet];
  const { copy } = useClipboard(selectedWallet?.virtualAccount.accountNumber);

  useEffect(() => {
    if (activeWallet > wallets.length - 1) {
      setActiveWallet(0);
    }
  }, [activeWallet, wallets.length]);

  useEffect(() => {
    if (props?.customer_id) {
      setActiveCustomerId(props.customer_id);
    }
  }, [props?.customer_id]);

  if (!props) return null;

  const openSetupResume = (customer: WalletCustomerCardData) => {
    if (!customer.setup_resume) return;

    openDialog(
      customer.entity_type === 'corporate' ? (
        <WalletKycDialog setupResume={customer.setup_resume} />
      ) : (
        <WalletIndividualKycDialog setupResume={customer.setup_resume} />
      ),
      {
        allowBackgroundDismiss: false,
      }
    );
  };

  const switchWallet = (walletOption: string) => {
    const [type, customerId, walletIndex] = walletOption.split(':');
    const selectedOption = relatedCustomers.find(
      (customer) => customer.customer_id === Number(customerId)
    );

    if (!selectedOption) return;

    if (type === 'setup') {
      openSetupResume(selectedOption);
      return;
    }

    setActiveCustomerId(selectedOption.customer_id);
    setActiveWallet(Number(walletIndex));
  };

  const walletOptions = relatedCustomers.flatMap((customer) => {
    if (customer.wallets.length > 0) {
      return customer.wallets.map(({ name }, idx) => ({
        label:
          relatedCustomers.length > 1
            ? `${customer.label}: ${name || `Wallet ${idx + 1}`}`
            : name || `Wallet ${idx + 1}`,
        value: `wallet:${customer.customer_id}:${idx}`,
      }));
    }

    if (customer.setup_resume) {
      return [
        {
          label: `${customer.label}: Continue setup`,
          value: `setup:${customer.customer_id}:0`,
        },
      ];
    }

    return [];
  });

  const selectedWalletOption = `wallet:${selectedCustomer?.customer_id || props.customer_id}:${activeWallet}`;

  if (
    selectedCustomer?.wallet_status == 'closed' ||
    selectedCustomer?.wallet_status == 'suspended'
  )
    return (
      <div className="p-4 sm:p-6 min-h-56 space-y-4 bg-customs-lavender-foreground dark:bg-background-accent border border-secondary rounded-lg  max-w-lg mx-auto">
        {/* 1. Status Banner */}
        <div className="flex items-center gap-4">
          <div className=" px-3 py-2 rounded-full border-4 border-warning/20">
            <IonIcon icon={lockClosedOutline} className="size-8 text-warning" />
          </div>
          <div>
            <h6 className="font-semibold high-emphasis-text leading-none">
              Wallet Temporarily {selectedCustomer?.wallet_status}
            </h6>
            <small className="medium-emphasis-text">
              Your account is restricted while we review your details.
            </small>
          </div>
        </div>

        {/* 3. Explanation & Context */}
        <div className="space-y-4 mb-10">
          <div className="flex gap-2 items-start p-4 bg-black/5 dark:bg-white/5 rounded-xl">
            <IonIcon
              icon={documentTextOutline}
              className="size-6 text-primary mt-0.5"
            />
            <div>
              <h5 className="font-medium text-sm high-emphasis-text">
                Why is this happening?
              </h5>
              <p className="text-xs medium-emphasis-text mt-1">
                Common reasons include unusual activity detection or required
                identity verification updates.
              </p>
            </div>
          </div>
        </div>

        {/* 4. Primary Actions */}
        <Link
          to="/dashboard/help&support/contact-support"
          className="w-full flex items-center justify-center gap-2.5 py-3 bg-primary text-white rounded-xl text-sm font-semibold hover:opacity-90 transition shadow-md shadow-primary/20"
        >
          <IonIcon icon={chatbubbleEllipsesOutline} className="size-4" />
          <span>Contact Support</span>
        </Link>
      </div>
    );

  return (
    <div className="p-4 sm:p-6 min-h-56 h-full bg-background dark:bg-background-accent border border-border rounded-lg overflow-hidden">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-text-dark-high dark:text-text-light-high">
          My Wallet
        </h2>
        {walletOptions.length > 1 && (
          <IonicInput
            onChange={(e: any) => {
              if (e.target?.value) switchWallet(e.target?.value);
            }}
            value={selectedWalletOption}
            type="select"
            placeholder="select wallet"
            size="sm"
            innerContainerClass="rounded-3xl"
            options={walletOptions}
          />
        )}
      </div>

      <div className="before:w-[calc(100%-2rem)] before:top-4 mb-8 before:-translate-x-1/2 before:left-1/2 before:h-full before:absolute relative before:rounded-lg before:z-[1] before:bg-customs-lavender-foreground before:dark:bg-background-accent before:border before:border-secondary">
        <div className="space-y-4 p-4 bg-customs-lavender-foreground dark:bg-background-accent border border-secondary rounded-lg relative z-[3]">
          <div className="flex justify-between items-start">
            <div>
              <small className="block uppercase tracking-[0.1em] text-[10px] font-bold medium-emphasis-text mb-1">
                {selectedWallet?.name || `wallet ${activeWallet + 1}`}
              </small>
              <div className="flex flex-col">
                <p className="font-semibold high-emphasis-text  leading-tight">
                  {selectedWallet?.virtualAccount.bankName || `Bank Name`}
                </p>
                <div className="flex items-center w-full justify-between">
                  <p className="font-semibold medium-emphasis-text mr-1">
                    Acct No:{' '}
                  </p>
                  <p className="font-mono tracking-wide medium-emphasis-text">
                    {selectedWallet?.virtualAccount.accountNumber ||
                      `0123456789`}
                  </p>
                  <button
                    onClick={() =>
                      copy(
                        selectedWallet?.virtualAccount.accountNumber ||
                          `0123456789`
                      )
                    }
                    className="p-2"
                  >
                    <IonIcon
                      icon={copyOutline}
                      className="size-3 text-primary"
                    />
                  </button>
                </div>
              </div>
            </div>
            <div className="bg-white p-2 rounded-lg shadow-sm border border-secondary text-primary flex items-center justify-center">
              <IonIcon icon={walletOutline} className="size-8" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="space-y-1">
              <p className=" font-semibold medium-emphasis-text">Balance</p>
              <h3 className="font-bold mb-1 high-emphasis-text">
                {formatPrice(selectedWallet?.availableBalance || 0.0)}
              </h3>
            </div>

            <div className="flex items-center flex-wrap justify-between mb-4 medium-emphasis-text ">
              <div>
                <p>Ledger Balance:</p>
              </div>
              <p className="font-semibold">
                {formatPrice(selectedWallet?.ledgerBalance || 0.0)}
              </p>
            </div>
          </div>

          {/* <p className="flex items-center justify-between mb-4 medium-emphasis-text">
            <span>Increase Than last week</span>
            <span className="flex items-center bg-transparent text-primary border border-primary px-2 py-1 rounded-full shadow-sm">
              <IonIcon icon={arrowUpOutline} className="mr-1 size-4" />
              0%
            </span>
          </p> */}

          <div className="grid grid-cols-3 gap-2">
            <button
              disabled
              className="disabled:opacity-50 disabled:hover:cursor-default flex items-center justify-center gap-2 py-2 bg-white border border-gray-300 rounded-lg text-sm text-text-dark-high font-medium hover:bg-gray-50 hover:cursor-pointer transition"
            >
              <span>Top Up</span>
              <IonIcon icon={downloadOutline} className="text-inherit" />
            </button>
            <button
              disabled={!selectedWallet?.virtualAccount.accountNumber}
              onClick={() =>
                openDialog(
                  <SendMoneyDialog
                    fromAccountNumber={
                      selectedWallet!.virtualAccount.accountNumber
                    }
                    availableBalance={selectedWallet?.availableBalance || 0}
                  />,
                  { allowBackgroundDismiss: false }
                )
              }
              className="disabled:opacity-50 disabled:hover:cursor-default flex items-center justify-center gap-2 py-2 bg-white border border-gray-300 rounded-lg text-sm text-text-dark-high font-medium hover:bg-gray-50 hover:cursor-pointer transition"
            >
              <span>Send</span>
              <IonIcon icon={sendOutline} className="text-inherit" />
            </button>
            <button
              onClick={() => openDialog(<WalletTransactionsDialog />)}
              className="flex items-center justify-center gap-2 py-2 bg-white border border-gray-300 rounded-lg text-sm text-text-dark-high font-medium hover:bg-gray-50 hover:cursor-pointer transition"
            >
              <span>History</span>
              <IonIcon icon={receiptOutline} className="text-inherit" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wallet;
